import React, { useState, createContext, useContext, useEffect, useRef, useCallback, useMemo, useId } from 'react';
import {
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogSurface,
    DialogBody,
    Link,
    Spinner,
    Skeleton,
    SkeletonItem
} from "@fluentui/react-components";
import { 
    PreviewLink20Filled,
    Open20Filled,
    Dismiss20Regular
} from "@fluentui/react-icons";
import { IContainerItem } from "../../common/ContainerSchemas";
import { ItemsApiProvider } from "../../providers/ItemsApiProvider";
import { isPreviewableFile } from "../../common/Utils";
import './ContainerItemPreview.css';
import { useContainerItemPreviewStyles } from './ContainerItemPreview.styles';
import { Constants } from '../../common/Constants';

const itemsApi = ItemsApiProvider.instance;

// Context for managing preview state across components
interface PreviewContextType {
    openPreview: (item: IContainerItem, containerId: string) => void;
}

export const PreviewContext = createContext<PreviewContextType | null>(null);

interface ContainerItemPreviewProps {
    item: IContainerItem;
    stopPropagation: (e: React.MouseEvent) => void;
    containerId: string;
}

export const ContainerItemPreviewMenuItem: React.FC<ContainerItemPreviewProps> = ({ 
    item, 
    stopPropagation,
    containerId: containerId
}) => {
    const previewContext = useContext(PreviewContext);
    const onFilePreviewClick = useCallback((e: React.MouseEvent) => {
        stopPropagation(e);
        if (item.type === 'folder') {
            return;
        }
        previewContext?.openPreview(item, containerId);
    }, [stopPropagation, item, containerId, previewContext]);

    if (item.type === 'folder' || !isPreviewableFile(item.name)) {
        return null;
    }

    return (
        <MenuItem 
            icon={<PreviewLink20Filled />}
            onClick={onFilePreviewClick}
            data-testid="menu-item-preview-in-web"
        >
            Preview in web
        </MenuItem>
    );
};

interface PreviewProviderProps {
    children: React.ReactNode;
}

export const PreviewProvider: React.FC<PreviewProviderProps> = ({ children }) => {
    const [error, setError] = useState<Error | null>(null);
    const previewStyles = useContainerItemPreviewStyles();
    
    const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | undefined>(undefined);
    const [previewFile, setPreviewFile] = useState<IContainerItem | undefined>(undefined);
    const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
    
    const previewUrlRef = useRef<string | null>(null);
    const isOpeningRef = useRef<boolean>(false);
	const isWebComponent = Constants.IS_WEBCOMPONENT;

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
                previewUrlRef.current = null;
            }
        };
    }, []);

    const openPreview = useCallback(async (item: IContainerItem, containerId: string) => {
        if (isOpeningRef.current) {
            return;
        }

		// Prevent file preview in web component as the current approach of using preview using blob is not supported in web component/teamsapp.
		if(isWebComponent) {
			setError(new Error('Sorry, file preview is supported only in the standalone application.'));
			return;
		}

        if (!isPreviewableFile(item.name)) {
            console.warn(`Preview not supported for file type: ${item.name}`);
            setError(new Error(`Preview is only available for PDF and image files. "${item.name}" is not supported.`));
            return;
        }

        setIsPreviewLoading(true);
        isOpeningRef.current = true;
        
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        
        try {
            const downloadResult = await itemsApi.download(item.container_id || containerId, item.id);
            setPreviewFile(item);
            setPreviewBlob(downloadResult);
            
            previewUrlRef.current = URL.createObjectURL(downloadResult);
            
            setIsPreviewOpen(true);
        } catch (err) {
            console.error("Error opening file preview:", err);
            setError(new Error(`Failed to open preview for "${item.name}". Please try again.`));
        } finally {
            setIsPreviewLoading(false);
            isOpeningRef.current = false;
        }
    }, []);

    const closePreview = useCallback(() => {
        setIsPreviewOpen(false);
        setPreviewBlob(undefined);
        setPreviewFile(undefined);
        
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (error) {
            throw error;
        }
    }, [error]);

    const contextValue: PreviewContextType = useMemo(() => ({
        openPreview
    }), [openPreview]);

    const dialogTitleId = useId();

    return (
        <PreviewContext.Provider value={contextValue}>
            {children}
            
            {isPreviewLoading && (
                <div className="overlay" aria-live="polite" aria-busy="true">
                    <Spinner size="large" label="Loading..." />
                </div>
            )}

            <Dialog open={isPreviewOpen} onOpenChange={(_, data) => { if (!data.open) closePreview(); }} modalType="alert">
                <DialogSurface className={`kt-ui-app file-preview-modal ${previewStyles.modalSurface}`} aria-labelledby={dialogTitleId}>
                    <DialogBody>
                        <DialogTitle className="dialogTitle" id={dialogTitleId}>File Preview</DialogTitle>
                        <Dismiss20Regular 
                            className="dismissIcon" 
                            role="button"
                            tabIndex={0}
                            aria-label="Close preview"
                            onClick={closePreview} 
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); closePreview(); } }}
                            data-testid="dismiss-icon" 
                        />
                        <DialogContent>
                            {previewFile && (
                                <>
                                    <p className="filePreviewModalLink">
                                        {previewBlob && previewUrlRef.current && (
                                            <Link 
                                                href={previewUrlRef.current} 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {previewFile.name}
                                                <Open20Filled className="openIcon" />
                                            </Link>
                                        )}
                                        {!previewBlob && <>{previewFile.name}</>}
                                    </p>
                                    {previewBlob && previewUrlRef.current && (
                                        <iframe 
                                            title="file-preview" 
                                            src={previewUrlRef.current} 
                                            className="filePreviewModalIframe" 
                                        />
                                    )}
                                    {!previewBlob && (
                                        <div className="filePreviewModalSkeleton">
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth75}`}> <SkeletonItem /> </Skeleton>
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth75}`}> <SkeletonItem /> </Skeleton>
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth50}`}> <SkeletonItem /> </Skeleton>
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth50}`}> <SkeletonItem /> </Skeleton>
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth25}`}> <SkeletonItem /> </Skeleton>
                                            <Skeleton className={`skeleton ${previewStyles.skeletonWidth25}`}> <SkeletonItem /> </Skeleton>
                                        </div>
                                    )}
                                </>
                            )}

						</DialogContent>
					</DialogBody>
                </DialogSurface>
            </Dialog>
        </PreviewContext.Provider>
    );
};

export const ContainerItemPreview = ContainerItemPreviewMenuItem;

export default ContainerItemPreview;
